import { useEffect, useRef, useState } from "react";
import {
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  Navigate,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { EhrConnector, getMe } from "@elation/api-client/src/stitch-web";
import { useSyncPatientByEhr } from "@elation/api-client/src/stitch-web/stitchWeb";

import { LoginPage, type Values } from "@src/deprecated/pages/Login";
import { LoginIframePage } from "@src/deprecated/pages/LoginIframe";
import { IS_CHROME_EXTENSION, IS_IN_IFRAME, OKTA_SCOPES } from "@src/env";
import {
  authClient,
  BEARER_TOKEN_KEY,
  getDefaultPostAuthSuccessDestination,
  resolvePostAuthDestination,
  type Session,
  waitForSession,
} from "@src/features/auth";
import {
  sendIframeEvent,
  useAuthTokenFromParent,
  useEhrFromIframe,
  useIframeLoginState,
  useIsIframeInitialized,
  usePatientFromIframe,
} from "@src/features/iframe";
import { navigation } from "@src/shared/navigation";
import { appStore } from "@src/shared/stores/app";
import { toErrorMessage } from "@src/shared/utils/errors";
import { events } from "@src/shared/utils/observability/events";
import {
  iframeLoginMethod,
  type LoginMethod,
  trackSessionInitFailure,
  trackUserLoggedIn,
} from "@src/shared/utils/observability/trackUserLoggedIn";

export function meta() {
  return [{ title: "Login" }, { name: "description", content: "Login" }];
}

export function clientLoader(_: LoaderFunctionArgs) {
  return {
    initialEmail: process.env.LOCAL_EMAIL || "",
    initialPassword: process.env.LOCAL_PASSWORD || "",
  };
}

export async function clientAction(_args: ActionFunctionArgs) {
  return {};
}

function trackIframeLogin(ehr: EhrConnector) {
  const method = iframeLoginMethod(ehr);
  getMe()
    .then(({ data: me }) => trackUserLoggedIn(me, method))
    .catch((err) => {
      console.error("Failed to record login analytics:", err);
      trackSessionInitFailure(method, err);
    });
}

function IframeLoginFlow(props: {
  isLoading: boolean;
  error?: string | null;
  session: Session | null;
}) {
  const { isLoading, error, session } = props;

  const iframeState = useIframeLoginState();
  const patientFromIframe = usePatientFromIframe();
  const ehrFromIframe = useEhrFromIframe();

  const isIframeInitialized = useIsIframeInitialized();

  const syncPatient = useSyncPatientByEhr();
  const { mutate: syncMutate, isPending: isSyncLoading } = syncPatient;
  const isSyncingIframePatient = iframeState.matches("syncing");

  const firedForRef = useRef<string | null>(null);

  const handleRetry = () => {
    localStorage.removeItem(BEARER_TOKEN_KEY);
    window.location.reload();
  };

  useEffect(() => {
    appStore.getState().setPracticeTimezone(undefined);
  }, []);

  useEffect(
    function notifySessionState() {
      if (!IS_IN_IFRAME) return;

      if (session) {
        sendIframeEvent({ type: "SESSION_READY" });
      } else {
        sendIframeEvent({ type: "SESSION_MISSING" });
      }
    },
    [session],
  );

  useEffect(
    function triggerSyncOnce() {
      if (!isSyncingIframePatient) return;
      if (!ehrFromIframe || !patientFromIframe?.id) return;

      const fireKey = `${ehrFromIframe}:${patientFromIframe.id}`;

      if (firedForRef.current === fireKey) {
        return;
      }

      firedForRef.current = fireKey;

      syncMutate(
        {
          ehr: ehrFromIframe as EhrConnector,
          patientId: patientFromIframe.id.toString(),
        },
        {
          onSuccess: (data) => {
            sendIframeEvent({
              type: "SYNC_SUCCESS",
              patient: data.data.patient,
            });
          },
          onError: (err) => {
            sendIframeEvent({
              type: "SYNC_ERROR",
              error: toErrorMessage(err),
            });
          },
        },
      );
    },
    [isSyncingIframePatient, ehrFromIframe, patientFromIframe, syncMutate],
  );

  useEffect(
    function trackLoginOnInitialization() {
      if (!isIframeInitialized) return;
      if (!ehrFromIframe) return;

      trackIframeLogin(ehrFromIframe as EhrConnector);
    },
    [isIframeInitialized, ehrFromIframe],
  );

  if (iframeState.matches("redirectToPatients")) {
    return <Navigate to={navigation.patients()} replace />;
  }

  if (iframeState.matches("redirectToBriefing")) {
    const stitchPatient = iframeState.context.stitchPatient;

    if (stitchPatient) {
      return (
        <Navigate
          to={navigation.patientBriefing(stitchPatient.id.toString())}
          replace
        />
      );
    }
  }

  if (iframeState.matches("error")) {
    return (
      <LoginIframePage
        isLoading={false}
        error={iframeState.context.error}
        onRetry={handleRetry}
      />
    );
  }

  if (iframeState.matches("initializing")) {
    return <LoginIframePage isLoading />;
  }

  const displayError = error ?? null;

  return (
    <LoginIframePage
      isLoading={isLoading || isSyncLoading}
      error={displayError}
      onRetry={displayError ? handleRetry : undefined}
    />
  );
}

function AuthenticatedLoginRedirect() {
  const [destination, setDestination] = useState<string | null>(null);
  const successDestination = getDefaultPostAuthSuccessDestination();

  useEffect(() => {
    let cancelled = false;

    void resolvePostAuthDestination(successDestination, {
      route: "login",
      operation: "existingSessionRedirect",
    })
      .then((resolved) => {
        if (!cancelled) {
          setDestination(resolved);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDestination(successDestination);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [successDestination]);

  if (!destination) {
    return <LoginPage isLoading />;
  }

  return <Navigate to={destination} replace />;
}

export default function LoginForClient() {
  const {
    data: session,
    isPending: isPendingSession,
    error: sessionError,
    refetch,
  } = authClient.useSession();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const errorFromUrl = searchParams.get("error");

  const sessionErrorMessage = sessionError?.message
    ? "Unable to verify your session. Please try logging in again."
    : undefined;

  const loginError = errorFromUrl || error || sessionErrorMessage;

  const trackLoginSuccess = async (method: LoginMethod) => {
    try {
      const { data: me } = await getMe();

      trackUserLoggedIn(me, method);
    } catch (err) {
      console.error("Failed to record login analytics:", err);
      trackSessionInitFailure(method, err);
    }
  };

  const handleLoginWithStitch = async (values: Values) => {
    try {
      const result = await authClient.signIn.email(values, {
        onSuccess: async () => {
          await Promise.all([
            trackLoginSuccess("email"),
            // Prime the session store before navigating (STITCH-426).
            waitForSession(),
          ]);
          const destination = await resolvePostAuthDestination(
            getDefaultPostAuthSuccessDestination(),
            { route: "login", operation: "emailSignIn" },
          );
          navigate(destination, { replace: true });
        },
      });

      if (result.error) {
        const errorMsg = result.error.message ?? "An unknown error occurred";

        setError(errorMsg);

        events.loginFailed.track({ method: "email", error: errorMsg });
      }
    } catch (error) {
      const errorMsg = toErrorMessage(error);

      setError(errorMsg);

      events.loginFailed.track({ method: "email", error: errorMsg });
    }
  };

  const handleOktaLogin = async (_values: Values) => {
    setIsLoading(true);
    setError(null);

    try {
      const oktaCallbackUrl = `${window.location.origin}/login/callback`;
      await authClient.signIn.oauth2({
        providerId: "okta",
        callbackURL: oktaCallbackUrl,
        newUserCallbackURL: oktaCallbackUrl,
        scopes: OKTA_SCOPES,
      });
    } catch (err) {
      console.error("Login error:", err);

      const errorMsg = toErrorMessage(err);

      setError(errorMsg);
      events.loginFailed.track({ method: "okta_web", error: errorMsg });
      setIsLoading(false);
    }
  };

  useAuthTokenFromParent(refetch);

  if (IS_IN_IFRAME) {
    return (
      <IframeLoginFlow
        isLoading={isLoading || isPendingSession}
        error={loginError}
        session={session}
      />
    );
  }

  if (session && !loginError) {
    return <AuthenticatedLoginRedirect />;
  }

  return (
    <LoginPage
      onLogin={handleLoginWithStitch}
      onLoginWithElation={IS_CHROME_EXTENSION ? undefined : handleOktaLogin}
      isLoading={isLoading || isPendingSession}
      error={loginError}
    />
  );
}
